/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { forwardRef } from 'react';
import { useId } from 'react-aria';
import { OverlayArrow, Popover as AriaPopover } from 'react-aria-components';
import { PopoverProps } from './types';
import { useDefinition } from '../../hooks/useDefinition';
import { PopoverDefinition } from './definition';
import { Box } from '../Box';
import { BgReset } from '../../hooks/useBg';

/**
 * A popover component built on React Aria Components that displays floating
 * content anchored to a trigger element.
 *
 * @remarks
 * The Popover component supports multiple placements (top, right, bottom, left),
 * automatic viewport-constrained scrolling, and conditional arrow rendering. It
 * automatically handles positioning, collision detection, and ARIA attributes for
 * accessibility. Content is automatically padded and scrollable when it exceeds
 * available space.
 *
 * @example
 * Basic usage with DialogTrigger:
 * ```tsx
 * <DialogTrigger>
 *   <Button>Open Popover</Button>
 *   <Popover>
 *     <Text>Popover content</Text>
 *   </Popover>
 * </DialogTrigger>
 * ```
 *
 * @example
 * With custom placement and no arrow:
 * ```tsx
 * <DialogTrigger>
 *   <Button>Open</Button>
 *   <Popover placement="right" hideArrow>
 *     <Text>Content without arrow</Text>
 *   </Popover>
 * </DialogTrigger>
 * ```
 *
 * @public
 */
export const Popover = forwardRef<HTMLDivElement, PopoverProps>(
  (props, ref) => {
    const { ownProps, restProps } = useDefinition(PopoverDefinition, props);
    const { classes, children, hideArrow } = ownProps;
    const svgPathId = useId();

    return (
      <AriaPopover className={classes.root} {...restProps} ref={ref}>
        {({ trigger }) => (
          <>
            {!hideArrow &&
              trigger !== 'MenuTrigger' &&
              trigger !== 'SubmenuTrigger' && (
                <OverlayArrow className={classes.arrow}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <defs>
                      <path
                        id={svgPathId}
                        fillRule="evenodd"
                        d="M10.3356 7.39793L15.1924 3.02682C15.9269 2.36577 16.8801 2 17.8683 2H20V7.94781e-07L1.74846e-07 -9.53674e-07L0 2L1.4651 2C2.4532 2 3.4064 2.36577 4.1409 3.02682L8.9977 7.39793C9.378 7.7402 9.9553 7.74021 10.3356 7.39793Z M11.0046 8.14124C10.2439 8.82575 9.08939 8.82578 8.32869 8.14122L3.47189 3.77011C2.92109 3.27432 2.20619 2.99999 1.46509 2.99999L4.10999 3L8.99769 7.39793C9.37799 7.7402 9.95529 7.7402 10.3356 7.39793L15.2226 3L17.8683 2.99999C17.1271 2.99999 16.4122 3.27432 15.8614 3.77011L11.0046 8.14124Z"
                      />
                    </defs>

                    <use href={`#${svgPathId}`} />
                    <use href={`#${svgPathId}`} />

                    <path d="M11.0046 8.14124C10.2439 8.82575 9.08939 8.82578 8.32869 8.14122L3.47189 3.77011C2.92109 3.27432 2.20619 2.99999 1.46509 2.99999L4.10999 3L8.99769 7.39793C9.37799 7.7402 9.95529 7.7402 10.3356 7.39793L15.2226 3L17.8683 2.99999C17.1271 2.99999 16.4122 3.27432 15.8614 3.77011L11.0046 8.14124Z" />
                  </svg>
                </OverlayArrow>
              )}
            <BgReset>
              <Box bg="neutral" className={classes.content}>
                {children}
              </Box>
            </BgReset>
          </>
        )}
      </AriaPopover>
    );
  },
);

Popover.displayName = 'Popover';
